import {
  Supplier,
  CreateSupplierRequest,
  UpdateSupplierRequest,
  CreatePaymentRequest,
  CreateDiscountRequest,
  SupplierFilters,
  SupplierListResponse,
  PaginatedResponse,
  StandardResponse,
  WarehouseReceiptUI,
  SupplierDebtSummary,
  SupplierDebtReportResponse,
  SupplierTransactionRecord
} from '@/types/supplier';
import { api } from '@/api/axios';
import queryString from 'query-string';

  // Transform API response to ensure consistent field naming
const transformSupplierResponse = (apiData: any): Supplier => {
  return {
    id: apiData.id,
    code: apiData.code,
    name: apiData.name,
    email: apiData.email,
    phone: apiData.phone,
    address: apiData.address,
    contactPerson: apiData.contactPerson || apiData.contact_person || '',
    status: apiData.status,
    createdAt: apiData.createdAt || apiData.created_at,
    updatedAt: apiData.updatedAt || apiData.updated_at,
    categoryGroupId: apiData.categoryGroupId || apiData.category_group_id,
    categoryGroupName: apiData.categoryGroupName || apiData.category_group_name,
    totalDebt: apiData.totalDebt || apiData.total_debt || 0,
    currentPayables: apiData.currentPayables || apiData.current_payables || 0,
    totalPurchase: apiData.totalPurchase || apiData.total_purchase || 0,
    lastPaymentDate: apiData.lastPaymentDate || apiData.last_payment_date,
    paymentStatus: apiData.paymentStatus || apiData.payment_status,
    note: apiData.note,
    city: apiData.city,
    country: apiData.country,
    website: apiData.website,
    taxId: apiData.taxId || apiData.tax_id,
    lastTransactionDate: apiData.lastTransactionDate || apiData.last_transaction_date,
    rating: apiData.rating,
    notes: apiData.notes || apiData.note, // Alias for backward compatibility
  };
};

export const supplierApi = {
  // Get suppliers list with filters and pagination
  getSuppliers: async (
    page: number = 1,
    pageSize: number = 10,
    filters?: SupplierFilters
  ): Promise<SupplierListResponse> => {
    const params: Record<string, any> = {
      page,
      page_size: pageSize,
    };

    // Add filters to params
    if (filters?.search) {
      params.search = filters.search;
    }
    if (filters?.status && filters.status.length > 0) {
      // API expects single status string, not array
      params.status = filters.status[0];
    }
    if (filters?.payment_status) {
      params.payment_status = filters.payment_status;
    }
    if (filters?.categoryGroupId) {
      params.category_group_id = filters.categoryGroupId;
    }
    if (filters?.sort_by) {
      params.sort_by = filters.sort_by;
    }
    if (filters?.sort_desc !== undefined) {
      params.sort_desc = filters.sort_desc;
    }

    const queryStr = queryString.stringify(params);
    const response = await api.get<PaginatedResponse<any>>(`/suppliers?${queryStr}`);

    // Transform the response data (defensive against null/undefined)
    const rawList = Array.isArray(response.data.data) ? response.data.data : [];
    const transformedData = rawList.map(transformSupplierResponse);

    return {
      ...response.data,
      data: transformedData
    };
  },

  // Get single supplier by code
  getSupplier: async (code: string): Promise<Supplier> => {
    const response = await api.get<StandardResponse<any>>(`/suppliers/${code}`);
    return transformSupplierResponse(response.data.data);
  },

  // Get supplier status counts
  getStatusCounts: async (): Promise<Record<string, number>> => {
    const response = await api.get<StandardResponse<Record<string, number>>>('/suppliers/status-counts');
    return response.data.data;
  },

  // Create new supplier
  createSupplier: async (data: CreateSupplierRequest): Promise<Supplier> => {
    const payload: any = {
      name: data.name,
      email: data.email,
      phone: data.phone,
      address: data.address,
      contactPerson: data.contactPerson,
      taxId: data.taxId,
      status: data.status,
      note: data.note,
    };
    if (data.categoryGroupId) {
      payload.category_group_id = data.categoryGroupId;
    }
    const response = await api.post<StandardResponse<any>>('/suppliers', payload);
    return transformSupplierResponse(response.data.data);
  },

  // Update supplier
  updateSupplier: async (code: string, data: UpdateSupplierRequest): Promise<Supplier> => {
    const payload: any = {};
    if (data.name !== undefined) payload.name = data.name;
    if (data.email !== undefined) payload.email = data.email;
    if (data.phone !== undefined) payload.phone = data.phone;
    if (data.address !== undefined) payload.address = data.address;
    if (data.contactPerson !== undefined) payload.contactPerson = data.contactPerson;
    if (data.taxId !== undefined) payload.taxId = data.taxId;
    if (data.status !== undefined) payload.status = data.status;
    if (data.note !== undefined) payload.note = data.note;
    if (data.categoryGroupId !== undefined) {
      payload.category_group_id = data.categoryGroupId;
    }
    const response = await api.put<StandardResponse<any>>(`/suppliers/${code}`, payload);
    return transformSupplierResponse(response.data.data);
  },
  // Delete supplier
  deleteSupplier: async (code: string): Promise<void> => {
    await api.delete(`/suppliers/${code}`);
  },

  // Create payment for supplier
  createPayment: async (supplierId: string, data: CreatePaymentRequest): Promise<void> => {
    await api.post<StandardResponse<any>>(`/suppliers/${supplierId}/payments`, data);
  },

  // Create discount for supplier
  createDiscount: async (supplierId: string, data: CreateDiscountRequest): Promise<void> => {
    await api.post<StandardResponse<any>>(`/suppliers/${supplierId}/discounts`, data);
  },

  // TODO: These endpoints are not available in the current API
  // Uncomment when backend implements these endpoints

  // Get supplier transactions
  // getSupplierTransactions: async (supplierId: string): Promise<SupplierTransaction[]> => {
  //   const response = await api.get<StandardResponse<SupplierTransaction[]>>(`/suppliers/${supplierId}/transactions`);
  //   return response.data.data;
  // },

  // Get supplier debts
  // getSupplierDebts: async (supplierId: string): Promise<SupplierDebt[]> => {
  //   const response = await api.get<StandardResponse<SupplierDebt[]>>(`/suppliers/${supplierId}/debts`);
  //   return response.data.data;
  // },

  // Get supplier detail
  // getSupplierDetail: async (supplierId: string): Promise<{ supplier: Supplier; transactions: SupplierTransaction[]; debts: SupplierDebt[] }> => {
  //   const response = await api.get<StandardResponse<{ supplier: Supplier; transactions: SupplierTransaction[]; debts: SupplierDebt[] }>>(`/suppliers/${supplierId}/detail`);
  //   return response.data.data;
  // },

  // Export debt data
  exportDebtData: async (options: any): Promise<Blob> => {
    const response = await api.post('/suppliers/export-debt', options, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Import suppliers from file
  importSuppliers: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/suppliers/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Export suppliers to file
  exportSuppliers: async (format: 'csv' | 'xlsx', filters?: SupplierFilters): Promise<Blob> => {
    const params: Record<string, any> = { format };

    // Add filters to export params
    if (filters?.search) {
      params.search = filters.search;
    }
    if (filters?.status && filters.status.length > 0) {
      params.status = filters.status[0];
    }
    if (filters?.payment_status) {
      params.payment_status = filters.payment_status;
    }
    if (filters?.categoryGroupId) {
      params.category_group_id = filters.categoryGroupId;
    }

    const queryStr = queryString.stringify(params);
    const response = await api.get(`/suppliers/export?${queryStr}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Get import template
  getImportTemplate: async (format: 'csv' | 'xlsx'): Promise<Blob> => {
    const response = await api.get(`/suppliers/import/template?format=${format}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // ============================================
  // Nested Table APIs (for SupplierNestedTable)
  // ============================================

  // Get warehouse receipts for a supplier (Purchase History Tab)
  getSupplierWarehouseReceipts: async (
    supplierCode: string,
    page: number = 1,
    pageSize: number = 10,
    includeReturns: boolean = true
  ): Promise<{ data: WarehouseReceiptUI[]; total: number }> => {
    const params: Record<string, any> = {
      page,
      page_size: pageSize,
      include_returns: includeReturns,
    };
    const queryStr = queryString.stringify(params);
    const response = await api.get<any>(`/suppliers/${supplierCode}/warehouse-receipts?${queryStr}`);
    
    // Transform response data
    const rawData = response.data?.data || [];
    const transformedData: WarehouseReceiptUI[] = rawData.map((item: any) => ({
      id: item.id,
      code: item.code,
      type: item.type || 'warehouse_receipt',
      supplierId: item.supplier_id || item.supplierId,
      supplierCode: item.supplier_code || item.supplierCode,
      supplierName: item.supplier_name || item.supplierName,
      totalAmount: item.total_amount || item.totalAmount || 0,
      status: item.status || 'pending',
      createdAt: item.created_at || item.createdAt,
      updatedAt: item.updated_at || item.updatedAt,
      note: item.note,
    }));

    return {
      data: transformedData,
      total: response.data?.total || 0,
    };
  },

  // Get supplier debt report (Debt Tab - transactions list)
  getSupplierDebtReport: async (params: {
    supplierCode: string;
    dateFrom: string;
    dateTo: string;
    page: number;
    pageSize: number;
  }): Promise<{ data: SupplierDebtReportResponse }> => {
    const queryParams: Record<string, any> = {
      date_from: params.dateFrom,
      date_to: params.dateTo,
      page: params.page,
      page_size: params.pageSize,
    };
    const queryStr = queryString.stringify(queryParams);
    const response = await api.get<any>(`/suppliers/${params.supplierCode}/debt-report?${queryStr}`);
    
    // Backend returns: StandardResponse{Data: SupplierDebtReportResponse{Data: [...], Total: ...}}
    // So structure is: response.data.data (SupplierDebtReportResponse)
    const debtReportData = response.data?.data;
    const rawData = debtReportData?.data || [];
    
    // Ensure rawData is an array
    if (!Array.isArray(rawData)) {
      console.error('Invalid response format - data is not an array:', rawData);
      return {
        data: {
          data: [],
          total: 0,
        },
      };
    }
    
    const transformedData: SupplierTransactionRecord[] = rawData.map((item: any) => ({
      id: item.id || item.ID,
      code: item.code || item.Code,
      record_type: item.record_type || item.RecordType || item.type,
      total: item.total || item.Total || item.total_amount || 0,
      paid_now: item.paid_now || item.PaidNow || item.paid_amount || 0,
      return_amount: item.return_amount || item.ReturnAmount || 0,
      created_at: item.created_at || item.CreatedAt || item.createdAt,
      note: item.note || item.Note,
      // Debt adjustment-specific fields
      adjustment_amount: item.adjustment_amount || item.AdjustmentAmount,
      adjustment_date: item.adjustment_date || item.AdjustmentDate,
      report_period_from: item.report_period_from || item.ReportPeriodFrom,
      report_period_to: item.report_period_to || item.ReportPeriodTo,
      reason: item.reason || item.Reason,
    }));

    return {
      data: {
        data: transformedData,
        total: debtReportData?.total || debtReportData?.Total || 0,
      },
    };
  },

  // Get supplier debt summary (Debt Tab - summary cards)
  getSupplierDebtSummary: async (params: {
    supplierCode: string;
    dateFrom: string;
    dateTo: string;
  }): Promise<SupplierDebtSummary> => {
    const queryParams: Record<string, any> = {
      date_from: params.dateFrom,
      date_to: params.dateTo,
    };
    const queryStr = queryString.stringify(queryParams);
    const response = await api.get<any>(`/suppliers/${params.supplierCode}/debt-summary?${queryStr}`);
    
    const data = response.data?.data || response.data || {};
    return {
      opening_balance: data.opening_balance || 0,
      warehouse_receipt_amount: data.warehouse_receipt_amount || data.purchase_amount || 0,
      payment_amount: data.payment_amount || 0,
      return_amount: data.return_amount || 0,
      closing_balance: data.closing_balance || 0,
    };
  },
};
